import { Button, Sheet, SheetContent, SheetHeader, SheetOverlay, SheetTitle } from '@parrot-software/design-system';
import { ExternalLink, SquarePen, Trash2 } from '@parrot-software/design-system/icons';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { VerificationBadge } from '@personnel/presentation/components/shared';
import type { EmployeeDeleteTarget, EmployeeListItem } from '@personnel/presentation/types';

interface EmployeeDetailSheetProps {
  employee: EmployeeListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete?: (employee?: EmployeeDeleteTarget) => void;
}

/** The role that owns every permission. It has no editable page, so the list never links to it. */
const SUPER_ADMIN_ROLE = 'Super Admin';

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
    <span className="min-w-0 text-right text-sm font-medium break-words">{value}</span>
  </div>
);

/**
 * The whole record of one user, and everything that can be done to them, as a bottom sheet.
 *
 * It is the mobile counterpart of a table row: the columns the card left out are read here, and the
 * actions that live in the row menu are full-width buttons at the bottom.
 *
 * It renders nothing without a user, so the list can keep one mounted instance and only toggle it.
 *
 * @param {EmployeeDetailSheetProps} props - Component properties
 */
export const EmployeeDetailSheet = ({
  employee,
  open,
  onOpenChange,
  onDelete,
}: EmployeeDetailSheetProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const history = useHistory();

  const canOpenRole = Boolean(employee?.roleUuid) && employee?.roleName !== SUPER_ADMIN_ROLE;

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetOverlay className="z-[1300] bg-black/50" />
      <SheetContent
        className="z-[1330] flex max-h-[90dvh] w-full max-w-full flex-col gap-4 overflow-y-auto rounded-t-2xl px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] font-sans"
        side="bottom"
      >
        {employee && (
          <>
            <SheetHeader className="mb-2 items-center justify-center gap-1 text-left">
              <SheetTitle className="text-xl font-bold">{employee.fullName}</SheetTitle>
              <VerificationBadge verificationStatus={employee.verificationStatus} />
            </SheetHeader>

            <div className="flex flex-col rounded-lg border border-border p-4">
              <DetailRow label={t('list.table.columns.email')} value={employee.email} />
              <DetailRow label={t('list.table.columns.role')} value={employee.roleName || '-'} />
              <DetailRow label={t('list.table.columns.stores')} value={employee.assignedStores ?? 0} />
            </div>

            <Button
              className="w-full"
              onClick={() => history.push(`/employees/${employee.uuid}/edit`)}
              variant="outline"
            >
              <SquarePen className="mr-1 size-4" />
              {t('list.mobile.editUser')}
            </Button>

            {canOpenRole && (
              <Button className="w-full" onClick={() => history.push(`/roles/${employee.roleUuid}`)} variant="outline">
                <ExternalLink className="mr-1 size-4" />
                {t('list.mobile.viewRole')}
              </Button>
            )}

            {onDelete && (
              <Button
                className="w-full"
                onClick={() =>
                  onDelete({
                    uuid: employee.uuid,
                    roleName: employee.roleName ?? undefined,
                    permissions: employee.permissions,
                  })
                }
                variant="destructive"
              >
                <Trash2 className="mr-1 size-4" />
                {t('actions.delete')}
              </Button>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
